<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Contact Us"
      subtitle="View and manage website inquiries from the Contact Us form."
      :headers="headers"
      :items="queries"
      :loading="loading"
      show-search
      search-placeholder="Search by name, email or message..."
    >
 

      <template v-slot:item.status="{ item }">
        <v-chip
          small
          label
          :color="getStatusColor(item.status)"
          text-color="white"
          class="font-weight-bold"
        >
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            icon="mdi-eye-outline"
            label="View"
            @click="openViewDialog(item)"
          />
          <PremiumActionButton
            icon="mdi-delete-outline"
            label="Delete"
            variant="danger"
            @click="openDelete(item._id || item.id)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- View/Update Modal -->
    <PremiumModalForm
      v-model="viewDialog"
      title="Inquiry Details"
      submit-text="Update Status"
      cancel-text="Close"
      :loading="saving"
      max-width="600"
      @submit="updateStatus"
    >
      <div v-if="selectedQuery" style="display:flex;flex-direction:column;gap:20px;">
        <!-- Info Cards -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:15px;">
           <div class="info-item">
               <span class="info-label">Name</span>
               <span class="info-value">{{ selectedQuery.name }}</span>
           </div>
           <div class="info-item">
               <span class="info-label">Mobile</span>
               <span class="info-value">{{ selectedQuery.mobile }}</span>
           </div>
           <div class="info-item" style="grid-column: span 2">
               <span class="info-label">Email</span>
               <span class="info-value text-lowercase">{{ selectedQuery.email }}</span>
           </div>
        </div>

        <div class="info-item">
            <span class="info-label">Message</span>
            <div class="message-box">{{ selectedQuery.message }}</div>
        </div>

        <v-divider></v-divider>

        <div>
            <PremiumInput
              v-model="statusForm.status"
              name="status"
              label="Update Status"
              is-select
              :items="statusOptions"
            />
        </div>
      </div>
    </PremiumModalForm>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete Inquiry"
      message="Are you sure you want to delete this inquiry?"
      confirmText="Yes, Delete"
      cancelText="Cancel"
      @confirm="deleteQuery"
      @cancel="deleteDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import moment from 'moment';
import PremiumTable from '../common/PremiumTable.vue';
import ConfirmModal from '../common/ConfirmModal.vue';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumActionButton from '../common/PremiumActionButton.vue';
import PremiumInput from '../common/PremiumInput.vue';

export default {
  name: 'ContactInquiries',
  components: { PremiumTable, ConfirmModal, PremiumModalForm, PremiumActionButton, PremiumInput },

  data() {
    return {
      loading:        false,
      saving:         false,
      queries:        [],
      viewDialog:     false,
      deleteDialog:   false,
      delete_id:      null,
      selectedQuery:  null,
      statusForm:     { status: 0 },
      currentFilter:  '',

      statusOptions: [
        { label: 'Pending',   value: 0 },
        { label: 'Resolved',  value: 1 },
        { label: 'Cancelled', value: 2 },
      ],

      filterItems: [
        { label: 'All Status', value: '' },
        { label: 'Pending',    value: 0 },
        { label: 'Resolved',   value: 1 },
        { label: 'Cancelled',  value: 2 },
      ],

      headers: [
        { text: 'Name',       value: 'name',       width: '15%' },
        { text: 'Email',      value: 'email',      width: '15%' },
        { text: 'Mobile',     value: 'mobile',     width: '12%' },
        { text: 'Message',    value: 'message',    width: '30%' },
        { text: 'Status',     value: 'status',     width: '12%' },
        { text: 'Date',       value: 'created_at', width: '10%' },
        { text: 'Actions',    value: 'actions',    width: '6%', sortable: false },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    getQueries() {
      this.loading = true;
      let url = 'superadmin/contact-us';
      if (this.currentFilter !== '') {
          url += `?status=${this.currentFilter}`;
      }
      
      this.$axios.get(url).then(res => {
        this.loading = false;
        this.queries = res.data;
      }).catch(() => {
        this.loading = false;
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: 'Failed to load inquiries.' });
      });
    },

    openViewDialog(item) {
      this.selectedQuery = item;
      this.statusForm.status = item.status;
      this.viewDialog = true;
    },

    updateStatus() {
      this.saving = true;
      this.$axios.put(`superadmin/contact-us/${this.selectedQuery._id || this.selectedQuery.id}`, this.statusForm).then(() => {
        this.saving = false;
        this.viewDialog = false;
        this.getQueries();
        this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: 'Status updated successfully.' });
      }).catch(() => {
        this.saving = false;
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: 'Failed to update status.' });
      });
    },

    openDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteQuery() {
      this.deleteDialog = false;
      this.$axios.delete(`superadmin/contact-us/${this.delete_id}`).then(() => {
        this.getQueries();
        this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: 'Deleted successfully.' });
      }).catch(() => {
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: 'Delete failed.' });
      });
    },

    getStatusLabel(status) {
      const opt = this.statusOptions.find(o => o.value === status);
      return opt ? opt.label : 'Unknown';
    },

    getStatusColor(status) {
      switch (status) {
        case 0: return 'amber darken-2'; // Pending
        case 1: return 'green darken-1'; // Resolved
        case 2: return 'blue-grey lighten-1'; // Cancelled
        default: return 'grey';
      }
    },

    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    }
  },

  mounted() {
    this.getQueries();
  },
};
</script>

<style scoped>
.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.info-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #94a3b8;
    letter-spacing: 0.5px;
}
.info-value {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
}
.message-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
    font-size: 14px;
    color: #334155;
    white-space: pre-wrap;
    min-height: 80px;
    max-height: 200px;
    overflow-y: auto;
}
</style>
